import { useState, useEffect, useCallback, useContext } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import Layout from '../components/Layout';
import { AuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmationModal from '../components/ConfirmationModal';
import MaxBadge from '../components/MaxBadge';
import { accountService } from '../services/accountService';
import { isMaxTierError } from '../utils/tier';
import {
  memoryService,
  MEMORY_DOMAINS,
  MEMORY_CATEGORIES,
  normalizeCategory,
  confidenceBand,
  type MemoryFact,
  type MemorySettings,
  type MemoryStats,
  type MemoryAuditEntry,
  type MemoryProfileBlock,
  type MemoryProfileLabel,
  type MemoryEntityCard,
  type MemoryEvidence,
  type MemoryRecallExplain,
  type MemoryCategoryV2,
  type MemoryDomain,
  type MemoryImportPlatform,
  type MemoryConfidence,
} from '../services/memoryService';

const CATEGORY_STYLE: Record<MemoryCategoryV2, string> = {
  fact: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  preference: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  instruction: 'bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-400',
  event: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  opinion: 'bg-pink-50 text-pink-700 dark:bg-pink-500/10 dark:text-pink-400',
};

const CONFIDENCE_STYLE: Record<MemoryConfidence, string> = {
  high: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  medium: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  low: 'bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-300',
};

/** Vendor names are brand names and stay as-is in every locale. */
const IMPORT_PLATFORM_NAME: Record<MemoryImportPlatform, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
};
const IMPORT_PLATFORMS: readonly MemoryImportPlatform[] = ['chatgpt', 'claude', 'gemini'];

type MainTab = 'profile' | 'memories' | 'timeline' | 'entities' | 'activity' | 'team';
type StatusFilter = 'active' | 'sensitive' | 'archived' | 'quarantined' | 'all';

const BTN_NEUTRAL = 'px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10';
const BTN_DANGER = 'px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20';
const CHIP_ON = 'bg-blue-600 text-white';
const CHIP_OFF = 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10';
const INPUT = 'px-3 py-2 rounded-lg text-sm border bg-transparent';
const INPUT_STYLE = { borderColor: 'var(--color-border)', color: 'var(--color-text)' } as const;

function fmt(ts: number | null | undefined): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

function fmtMonth(ts: number): string {
  try {
    return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  } catch {
    return '—';
  }
}

/** The date a memory is about: the event it describes, else when it became true, else when it was seen. */
function factDate(f: MemoryFact): number | null {
  return f.eventAt ?? f.validAt ?? f.observedAt ?? null;
}

function importSource(f: MemoryFact): MemoryImportPlatform | null {
  const source = typeof f.source === 'string' ? f.source : '';
  if (!source.startsWith('import:')) return null;
  const platform = source.slice('import:'.length) as MemoryImportPlatform;
  return IMPORT_PLATFORM_NAME[platform] ? platform : null;
}

function monthKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function MemoryPage() {
  const { t } = useLingui();
  const CATEGORY_LABEL: Record<MemoryCategoryV2, string> = {
    fact: t`Fact`,
    preference: t`Preference`,
    instruction: t`Instruction`,
    event: t`Event`,
    opinion: t`Opinion`,
  };
  const CONFIDENCE_LABEL: Record<MemoryConfidence, string> = {
    high: t`High confidence`,
    medium: t`Medium confidence`,
    low: t`Low confidence`,
  };
  const DOMAIN_LABEL: Record<MemoryDomain, string> = {
    work: t`Work`,
    coding: t`Coding`,
    health: t`Health`,
    legal: t`Legal`,
    finance: t`Finance`,
    family: t`Family`,
    education: t`Education`,
    travel: t`Travel`,
    creative: t`Creative`,
    general: t`General`,
  };
  const PROFILE_LABEL: Record<MemoryProfileLabel, string> = {
    working_context: t`Working context`,
    response_preferences: t`Response preferences`,
    standing_requests: t`Standing requests`,
    current_focus: t`Current focus`,
  };
  const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
    active: t`Active`,
    sensitive: t`Held back (sensitive)`,
    archived: t`Archived`,
    quarantined: t`Quarantined`,
    all: t`All`,
  };
  const { showToast } = useToast();
  const { user } = useContext(AuthContext);
  const [facts, setFacts] = useState<MemoryFact[]>([]);
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [settings, setSettings] = useState<MemorySettings | null>(null);
  const [audit, setAudit] = useState<MemoryAuditEntry[]>([]);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<MemoryFact | null>(null);
  const [editText, setEditText] = useState('');
  const [confirmErase, setConfirmErase] = useState(false);
  const [tab, setTab] = useState<MainTab>('memories');
  // Evidence "popover": one open at a time, keyed by fact id.
  const [evidenceFor, setEvidenceFor] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<Record<string, MemoryEvidence | null>>({});
  // "Remember something"
  const [rememberText, setRememberText] = useState('');
  const [rememberCategory, setRememberCategory] = useState<MemoryCategoryV2>('fact');
  const [remembering, setRemembering] = useState(false);
  // Profile (the four compiled blocks)
  const [profile, setProfile] = useState<MemoryProfileBlock[]>([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileDraft, setProfileDraft] = useState<Partial<Record<MemoryProfileLabel, { value: string; locked: boolean }>>>({});
  const [profileSaving, setProfileSaving] = useState<MemoryProfileLabel | 'refresh' | null>(null);
  // Timeline
  const [timeline, setTimeline] = useState<MemoryFact[]>([]);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  // People & projects
  const [entities, setEntities] = useState<MemoryEntityCard[]>([]);
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const [confirmEntity, setConfirmEntity] = useState<MemoryEntityCard | null>(null);
  const [confirmDomain, setConfirmDomain] = useState<MemoryDomain | null>(null);
  // Import ("memory that follows you")
  const [importPlatform, setImportPlatform] = useState<MemoryImportPlatform>('chatgpt');
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  // Team memory (EPIC-07, Max only). The tab renders only for effectiveTier
  // 'max'; the API 403 MAX_TIER_REQUIRED stays the real gate.
  const [isMax, setIsMax] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [teamFacts, setTeamFacts] = useState<MemoryFact[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [multiplayerSaving, setMultiplayerSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [factList, statsData, settingsData] = await Promise.all([
        memoryService.getFacts({ all: true, limit: 500 }),
        memoryService.getStats(),
        memoryService.getSettings(),
      ]);
      setFacts(factList);
      setStats(statsData);
      setSettings(settingsData);
    } catch {
      showToast(t`Failed to load your memory`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    accountService.getAccountStatus().then((account) => {
      if (cancelled) return;
      setIsMax(account?.effectiveTier === 'max');
      setIsAdmin(!!account?.isAdmin);
    }).catch(() => { /* tier stays pro; tab stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  const loadTeamFacts = useCallback(async () => {
    setTeamLoading(true);
    try {
      setTeamFacts(await memoryService.getTeamFacts(500));
      setTeamLoaded(true);
    } catch (error: unknown) {
      // A 403 MAX_TIER_REQUIRED means the seat lost Max between render and
      // request: fall back to the personal view instead of erroring.
      if (isMaxTierError(error)) {
        setIsMax(false);
        setTab('memories');
      } else {
        showToast(t`Failed to load team memory`, 'error');
      }
    } finally {
      setTeamLoading(false);
    }
  }, [showToast]);

  const loadProfile = useCallback(async () => {
    try {
      const blocks = await memoryService.getProfile();
      setProfile(blocks);
      const draft: Partial<Record<MemoryProfileLabel, { value: string; locked: boolean }>> = {};
      for (const b of blocks) draft[b.label] = { value: b.value, locked: b.locked };
      setProfileDraft(draft);
      setProfileLoaded(true);
    } catch {
      showToast(t`Failed to load your profile`, 'error');
    }
  }, [showToast]);

  const loadTimeline = useCallback(async () => {
    try {
      setTimeline(await memoryService.getTimeline());
      setTimelineLoaded(true);
    } catch {
      showToast(t`Failed to load the timeline`, 'error');
    }
  }, [showToast]);

  const loadEntities = useCallback(async () => {
    try {
      setEntities(await memoryService.getEntities());
      setEntitiesLoaded(true);
    } catch {
      showToast(t`Failed to load people and projects`, 'error');
    }
  }, [showToast]);

  const loadAudit = useCallback(async () => {
    try {
      setAudit(await memoryService.getAudit(100));
      setAuditLoaded(true);
    } catch {
      showToast(t`Failed to load audit trail`, 'error');
    }
  }, [showToast]);

  useEffect(() => {
    if (tab === 'team' && isMax && !teamLoaded && !teamLoading) loadTeamFacts();
    if (tab === 'profile' && !profileLoaded) loadProfile();
    if (tab === 'timeline' && !timelineLoaded) loadTimeline();
    if (tab === 'entities' && !entitiesLoaded) loadEntities();
    if (tab === 'activity' && !auditLoaded) loadAudit();
  }, [tab, isMax, teamLoaded, teamLoading, loadTeamFacts, profileLoaded, loadProfile, timelineLoaded, loadTimeline, entitiesLoaded, loadEntities, auditLoaded, loadAudit]);

  const handleShare = async (fact: MemoryFact) => {
    try {
      await memoryService.shareFact(fact.id);
      showToast(t`Memory shared with your team`, 'success');
      if (teamLoaded) loadTeamFacts();
    } catch (error: unknown) {
      if (isMaxTierError(error)) {
        showToast(t`PrivacyPal Max is required to share memories`, 'error');
      } else {
        showToast(t`Failed to share memory`, 'error');
      }
    }
  };

  const handleTeamDelete = async (fact: MemoryFact) => {
    try {
      await memoryService.deleteTeamFact(fact.id);
      setTeamFacts((prev) => prev.filter((f) => f.id !== fact.id));
      showToast(t`Team memory removed`, 'success');
    } catch {
      showToast(t`Failed to remove team memory`, 'error');
    }
  };

  /**
   * Team memory opt-in (EPIC-07 section 5). The switch moves optimistically so
   * it feels immediate, and any failure puts the previous settings back. A 403
   * MAX_TIER_REQUIRED means the seat is not Max any more, so the Max surfaces
   * are dropped along with the toggle.
   */
  const handleMultiplayerToggle = async (next: boolean) => {
    if (multiplayerSaving) return;
    const previous = settings;
    if (previous) setSettings({ ...previous, memoryMultiplayerEnabled: next });
    setMultiplayerSaving(true);
    try {
      const updated = await memoryService.updateSettings({ memoryMultiplayerEnabled: next });
      setSettings(updated);
      showToast(
        next
          ? t`Team memory is on. Only the memories you share are visible to teammates.`
          : t`Team memory is off. Your memory is personal again.`,
        'success',
      );
    } catch (error: unknown) {
      if (previous) setSettings(previous);
      if (isMaxTierError(error)) {
        setIsMax(false);
        setTab('memories');
        showToast(t`PrivacyPal Max is required for team memory. Ask your admin to upgrade your seat.`, 'error');
      } else {
        showToast(t`Failed to update team memory`, 'error');
      }
    } finally {
      setMultiplayerSaving(false);
    }
  };

  const patchSettings = async (patch: Partial<MemorySettings>) => {
    setSaving(true);
    try {
      const updated = await memoryService.updateSettings(patch);
      setSettings(updated);
      showToast(t`Memory settings updated`, 'success');
    } catch {
      showToast(t`Failed to update memory settings`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleSensitiveDomain = (domain: MemoryDomain) => {
    const current = settings?.memorySensitiveDomains ?? [];
    const next = current.includes(domain) ? current.filter((d) => d !== domain) : [...current, domain];
    patchSettings({ memorySensitiveDomains: next });
  };

  const handleForgetDomain = async () => {
    const domain = confirmDomain;
    setConfirmDomain(null);
    if (!domain) return;
    try {
      await memoryService.forgetDomain(domain);
      showToast(t`Everything about this topic was forgotten`, 'success');
      load();
      setTimelineLoaded(false);
      setEntitiesLoaded(false);
    } catch {
      showToast(t`Failed to forget this topic`, 'error');
    }
  };

  const handleDelete = async (fact: MemoryFact) => {
    try {
      await memoryService.deleteFact(fact.id);
      setFacts((prev) => prev.filter((f) => f.id !== fact.id));
      showToast(t`Memory deleted`, 'success');
    } catch {
      showToast(t`Failed to delete memory`, 'error');
    }
  };

  const handlePin = async (fact: MemoryFact) => {
    const next = !fact.pinned;
    try {
      await memoryService.pinFact(fact.id, next);
      setFacts((prev) => prev.map((f) => (f.id === fact.id ? { ...f, pinned: next } : f)));
      showToast(next ? t`Memory pinned` : t`Memory unpinned`, 'success');
    } catch {
      showToast(t`Failed to update pin`, 'error');
    }
  };

  const handleArchive = async (fact: MemoryFact) => {
    try {
      await memoryService.archiveFact(fact.id);
      setFacts((prev) => prev.map((f) => (f.id === fact.id ? { ...f, status: 'archived' } : f)));
      showToast(t`Memory archived`, 'success');
    } catch {
      showToast(t`Failed to archive memory`, 'error');
    }
  };

  const handleRestore = async (fact: MemoryFact) => {
    try {
      await memoryService.restoreFact(fact.id);
      setFacts((prev) => prev.map((f) => (f.id === fact.id ? { ...f, status: 'active' } : f)));
      showToast(t`Memory restored`, 'success');
    } catch {
      showToast(t`Failed to restore memory`, 'error');
    }
  };

  const toggleEvidence = async (fact: MemoryFact) => {
    if (evidenceFor === fact.id) {
      setEvidenceFor(null);
      return;
    }
    setEvidenceFor(fact.id);
    if (evidence[fact.id] !== undefined) return;
    try {
      const ev = await memoryService.getEvidence(fact.id);
      setEvidence((prev) => ({ ...prev, [fact.id]: ev }));
    } catch {
      setEvidence((prev) => ({ ...prev, [fact.id]: null }));
    }
  };

  const handleRemember = async () => {
    const text = rememberText.trim();
    if (!text || remembering) return;
    setRemembering(true);
    try {
      const result = await memoryService.remember(text, rememberCategory);
      setRememberText('');
      if (result?.status === 'quarantined' || result?.status === 'sensitive') {
        showToast(t`Saved, but held back for your review`, 'success');
      } else {
        showToast(t`Remembered`, 'success');
      }
      load();
    } catch {
      showToast(t`Failed to remember that`, 'error');
    } finally {
      setRemembering(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing || !editText.trim()) return;
    try {
      await memoryService.correctFact(editing.id, editText.trim());
      setEditing(null);
      setEditText('');
      showToast(t`Memory corrected`, 'success');
      load();
    } catch {
      showToast(t`Failed to correct memory`, 'error');
    }
  };

  const handleSaveProfile = async (label: MemoryProfileLabel) => {
    const draft = profileDraft[label];
    if (!draft) return;
    setProfileSaving(label);
    try {
      await memoryService.updateProfileBlock(label, { value: draft.value.trim(), locked: draft.locked });
      showToast(t`Profile updated`, 'success');
      await loadProfile();
    } catch {
      showToast(t`Failed to update your profile`, 'error');
    } finally {
      setProfileSaving(null);
    }
  };

  const handleRefreshProfile = async () => {
    setProfileSaving('refresh');
    try {
      await memoryService.refreshProfile();
      showToast(t`Profile refreshed from your memories`, 'success');
      await loadProfile();
    } catch {
      showToast(t`Failed to refresh your profile`, 'error');
    } finally {
      setProfileSaving(null);
    }
  };

  const handleForgetEntity = async () => {
    const entity = confirmEntity;
    setConfirmEntity(null);
    if (!entity) return;
    try {
      await memoryService.forgetEntity(entity.twinHash);
      setEntities((prev) => prev.filter((e) => e.twinHash !== entity.twinHash));
      showToast(t`Everything about ${entity.displayValue} was forgotten`, 'success');
      load();
      setTimelineLoaded(false);
    } catch {
      showToast(t`Failed to forget ${entity.displayValue}`, 'error');
    }
  };

  const handleImport = async () => {
    const text = importText.trim();
    if (!text || importing) return;
    setImporting(true);
    try {
      const twin = await memoryService.encodeForImport(text, importPlatform);
      await memoryService.importMemory(importPlatform, twin);
      setImportText('');
      showToast(t`Import started. New memories appear once they are distilled.`, 'success');
      load();
    } catch {
      showToast(t`Failed to import memory`, 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await memoryService.exportMemory();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'privacypal-memory-export.json';
      a.click();
      URL.revokeObjectURL(url);
      showToast(t`Memory exported`, 'success');
    } catch {
      showToast(t`Failed to export memory`, 'error');
    }
  };

  const handleErase = async () => {
    setConfirmErase(false);
    try {
      await memoryService.eraseMemory();
      showToast(t`Memory erased and encryption keys destroyed`, 'success');
      load();
      setProfileLoaded(false);
      setTimelineLoaded(false);
      setEntitiesLoaded(false);
      setAuditLoaded(false);
    } catch {
      showToast(t`Failed to erase memory`, 'error');
    }
  };

  const openAudit = () => {
    setTab('activity');
  };

  /**
   * `all=true` lists every status the API keeps in the main list; a status the
   * list did not carry (an older API only returns active + quarantined) is
   * fetched on demand and merged in by id.
   */
  const chooseStatus = async (next: StatusFilter) => {
    setStatusFilter(next);
    if (next === 'all' || next === 'active') return;
    if (facts.some((f) => f.status === next)) return;
    try {
      const extra = await memoryService.getFacts({ status: next, limit: 500 });
      if (extra.length === 0) return;
      setFacts((prev) => {
        const seen = new Set(prev.map((f) => f.id));
        return [...prev, ...extra.filter((f) => !seen.has(f.id))];
      });
    } catch { /* the list stays as loaded */ }
  };

  const toggleAuditRow = (id: string) => {
    setExpandedAudit((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const activeFacts = facts.filter((f) => f.status === 'active');
  const quarantined = facts.filter((f) => f.status === 'quarantined');
  const sensitive = facts.filter((f) => f.status === 'sensitive');
  const heldBack = quarantined.length + sensitive.length;
  const visible = facts
    .filter((f) => (statusFilter === 'all' ? f.status !== 'invalidated' : f.status === statusFilter))
    .filter((f) => filter === 'all' || normalizeCategory(f.category) === filter)
    .filter((f) => !search || f.displayText.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));

  // Rows grouped by the first entity they mention; memories about nothing in
  // particular come last.
  const groupedRows: { key: string | null; items: MemoryFact[] }[] = [];
  {
    const byEntity = new Map<string, MemoryFact[]>();
    const loose: MemoryFact[] = [];
    for (const f of visible) {
      const key = f.entityValues[0];
      if (!key) { loose.push(f); continue; }
      const bucket = byEntity.get(key);
      if (bucket) bucket.push(f); else byEntity.set(key, [f]);
    }
    for (const [key, items] of byEntity) groupedRows.push({ key, items });
    if (loose.length) groupedRows.push({ key: null, items: loose });
  }

  const timelineMonths: { key: string; label: string | null; items: MemoryFact[] }[] = [];
  {
    const byMonth = new Map<string, { label: string; items: MemoryFact[] }>();
    const undated: MemoryFact[] = [];
    const sorted = [...timeline].sort((a, b) => (factDate(b) ?? 0) - (factDate(a) ?? 0));
    for (const f of sorted) {
      const ts = factDate(f);
      if (!ts) { undated.push(f); continue; }
      const key = monthKey(ts);
      const bucket = byMonth.get(key);
      if (bucket) bucket.items.push(f); else byMonth.set(key, { label: fmtMonth(ts), items: [f] });
    }
    for (const [key, { label, items }] of byMonth) timelineMonths.push({ key, label, items });
    if (undated.length) timelineMonths.push({ key: 'undated', label: null, items: undated });
  }

  if (loading) {
    return (
      <Layout>
        <div className="loading"><div className="spinner" /></div>
      </Layout>
    );
  }

  const memoryOn = settings?.memoryEnabled ?? false;
  const multiplayerOn = settings?.memoryMultiplayerEnabled ?? false;
  const profileOn = settings?.memoryProfileEnabled ?? true;
  const sensitiveDomains = settings?.memorySensitiveDomains ?? [];

  const tabs: { key: MainTab; label: string; max?: boolean }[] = [
    { key: 'profile', label: t`Profile` },
    { key: 'memories', label: t`Memories` },
    { key: 'timeline', label: t`Timeline` },
    { key: 'entities', label: t`People & projects` },
    { key: 'activity', label: t`Activity` },
    ...(isMax ? [{ key: 'team' as const, label: t`Team memory`, max: true }] : []),
  ];

  const renderEntityChips = (fact: MemoryFact) => (
    fact.entityValues.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1.5">
        {fact.entityValues.map((v, i) => (
          <span
            key={`${fact.id}-${i}`}
            className="px-2 py-0.5 rounded text-[11px] bg-slate-100 dark:bg-white/5"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {v}
          </span>
        ))}
      </div>
    )
  );

  const renderCategoryChip = (fact: MemoryFact) => {
    const cat = normalizeCategory(fact.category);
    return (
      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${CATEGORY_STYLE[cat]}`}>
        {CATEGORY_LABEL[cat]}
      </span>
    );
  };

  const renderFactRow = (fact: MemoryFact) => {
    const band = confidenceBand(fact.confidence);
    const imported = importSource(fact);
    const ev = evidence[fact.id];
    return (
      <li key={fact.id} className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {renderCategoryChip(fact)}
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${CONFIDENCE_STYLE[band]}`}>
                {CONFIDENCE_LABEL[band]}
              </span>
              {fact.pinned && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-blue-600 text-white">
                  <Trans>Pinned</Trans>
                </span>
              )}
              {imported && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 dark:bg-white/5" style={{ color: 'var(--color-text-muted)' }}>
                  <Trans>imported from {IMPORT_PLATFORM_NAME[imported]}</Trans>
                </span>
              )}
              {fact.status === 'sensitive' && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400">
                  <Trans>Same-topic only</Trans>
                </span>
              )}
              {fact.platform && (
                <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{fact.platform}</span>
              )}
              <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                {fmt(factDate(fact))}
              </span>
              {fact.recallCount > 0 && (
                <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                  <Trans>used {fact.recallCount}×</Trans>
                </span>
              )}
            </div>
            <div className="text-sm" style={{ color: 'var(--color-text)' }}>{fact.displayText}</div>
            {renderEntityChips(fact)}
          </div>
          <div className="flex gap-2 flex-wrap sm:flex-shrink-0 sm:justify-end sm:max-w-[260px]">
            {isMax && fact.status === 'active' && (
              <button
                onClick={() => handleShare(fact)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{
                  border: '1px solid var(--color-max-border)',
                  backgroundColor: 'var(--color-max-bg)',
                  color: 'var(--color-max-text)',
                }}
                title={t`Share this memory with your team as stable twins`}
              >
                <Trans>Share</Trans>
              </button>
            )}
            <button onClick={() => toggleEvidence(fact)} className={BTN_NEUTRAL} style={{ color: 'var(--color-text)' }}>
              <Trans>Evidence</Trans>
            </button>
            {fact.status !== 'quarantined' && (
              <button onClick={() => handlePin(fact)} className={BTN_NEUTRAL} style={{ color: 'var(--color-text)' }}>
                {fact.pinned ? <Trans>Unpin</Trans> : <Trans>Pin</Trans>}
              </button>
            )}
            {fact.status === 'archived' ? (
              <button onClick={() => handleRestore(fact)} className={BTN_NEUTRAL} style={{ color: 'var(--color-text)' }}>
                <Trans>Restore</Trans>
              </button>
            ) : (
              <button onClick={() => handleArchive(fact)} className={BTN_NEUTRAL} style={{ color: 'var(--color-text)' }}>
                <Trans>Archive</Trans>
              </button>
            )}
            <button
              onClick={() => { setEditing(fact); setEditText(fact.displayText); }}
              className={BTN_NEUTRAL}
              style={{ color: 'var(--color-text)' }}
            >
              <Trans>Correct</Trans>
            </button>
            <button onClick={() => handleDelete(fact)} className={BTN_DANGER}>
              <Trans>Forget</Trans>
            </button>
          </div>
        </div>
        {evidenceFor === fact.id && (
          <div
            className="mt-3 rounded-xl p-3 text-xs border"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
          >
            {ev === undefined ? (
              <Trans>Loading evidence…</Trans>
            ) : ev === null || !ev.quote ? (
              <Trans>No evidence is stored for this memory. It may have been imported or typed in by you.</Trans>
            ) : (
              <>
                <div className="text-[10px] font-semibold uppercase tracking-wide mb-1"><Trans>Why PrivacyPal remembers this</Trans></div>
                <blockquote className="italic" style={{ color: 'var(--color-text)' }}>“{ev.quote}”</blockquote>
                <div className="mt-1.5 flex gap-3">
                  {ev.platform && <span>{ev.platform}</span>}
                  <span>{fmt(ev.observedAt)}</span>
                </div>
              </>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <Layout>
      <div className="animate-fade-in space-y-6">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}><Trans>Private Memory</Trans></h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>
              Memory that follows you across every AI. Stored as Privacy Twins: PrivacyPal holds the shape of what
              you know, never your secrets.
            </Trans>
          </p>
        </div>

        {/* Controls */}
        <div className="glass-panel rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="font-medium" style={{ color: 'var(--color-text)' }}>
                {memoryOn ? <Trans>Memory is on</Trans> : <Trans>Memory is off</Trans>}
              </div>
              <div className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                {memoryOn
                  ? <Trans>New conversations build your memory, and relevant memories are shared with the AI you are using.</Trans>
                  : <Trans>Turn memory on to carry context between ChatGPT, Claude, Gemini, Copilot, and your agents.</Trans>}
              </div>
            </div>
            <button
              onClick={() => patchSettings({ memoryEnabled: !memoryOn })}
              disabled={saving}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                memoryOn
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/20'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {memoryOn ? <Trans>Turn off memory</Trans> : <Trans>Turn on memory</Trans>}
            </button>
          </div>

          {memoryOn && (
            <>
              <div className="mt-6 pt-6 border-t grid gap-4 sm:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings?.memoryInjectionEnabled ?? true}
                    onChange={(e) => patchSettings({ memoryInjectionEnabled: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded"
                  />
                  <span>
                    <span className="text-sm font-medium block" style={{ color: 'var(--color-text)' }}><Trans>Use memory in prompts</Trans></span>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      <Trans>Adds relevant memories to what you send, in twin-space.</Trans>
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings?.memoryIncognito ?? false}
                    onChange={(e) => patchSettings({ memoryIncognito: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded"
                  />
                  <span>
                    <span className="text-sm font-medium block" style={{ color: 'var(--color-text)' }}><Trans>Incognito</Trans></span>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      <Trans>Pause capture and recall without deleting anything.</Trans>
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profileOn}
                    onChange={(e) => patchSettings({ memoryProfileEnabled: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded"
                  />
                  <span>
                    <span className="text-sm font-medium block" style={{ color: 'var(--color-text)' }}><Trans>Send my profile once per conversation</Trans></span>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      <Trans>Your four profile blocks open each new conversation. Relevant notes are added only when they match what you type.</Trans>
                    </span>
                  </span>
                </label>
              </div>

              {/* Sensitive topics: same-topic recall only, plus a per-topic forget. */}
              <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <div className="text-sm font-medium" style={{ color: 'var(--color-text)' }}><Trans>Sensitive topics</Trans></div>
                <p className="text-xs mt-1 mb-3" style={{ color: 'var(--color-text-muted)' }}>
                  <Trans>Notes on these topics may be recalled only in conversations on the same topic.</Trans>
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {MEMORY_DOMAINS.map((domain) => {
                    const on = sensitiveDomains.includes(domain);
                    return (
                      <div key={domain} className="flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => toggleSensitiveDomain(domain)}
                          disabled={saving}
                          aria-pressed={on}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${on ? CHIP_ON : CHIP_OFF}`}
                        >
                          {DOMAIN_LABEL[domain]}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDomain(domain)}
                          className="text-xs font-medium text-red-600 dark:text-red-400 whitespace-nowrap"
                        >
                          <Trans>Forget this topic</Trans>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Team memory opt-in (EPIC-07 section 5). Max seats only; the API
              403 MAX_TIER_REQUIRED stays the real gate. */}
          {isMax && (
            <div
              className="mt-6 pt-6 border-t flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <div className="max-w-2xl">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                    <Trans>Team memory (Multiplayer)</Trans>
                  </span>
                  <MaxBadge />
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {multiplayerOn
                    ? <Trans>On. Memories your teammates shared are used alongside your own, and teammates see only the memories you explicitly shared. Everything they see is Privacy Twins; real values never leave your vault.</Trans>
                    : <Trans>Off. Team memory is opt in: turn it on to use the memories your teammates shared. Nothing of yours becomes visible until you share a memory yourself, and what teammates see is Privacy Twins, never your real values.</Trans>}
                </p>
                {!memoryOn && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>Memory is off, so team memory takes effect once you turn memory on.</Trans>
                  </p>
                )}
              </div>
              <button
                onClick={() => handleMultiplayerToggle(!multiplayerOn)}
                disabled={multiplayerSaving}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 whitespace-nowrap self-start"
                style={{
                  border: '1px solid var(--color-max-border)',
                  backgroundColor: multiplayerOn ? 'transparent' : 'var(--color-max-bg)',
                  color: multiplayerOn ? 'var(--color-text)' : 'var(--color-max-text)',
                }}
              >
                {multiplayerOn ? <Trans>Turn off team memory</Trans> : <Trans>Turn on team memory</Trans>}
              </button>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t`Memories`, value: activeFacts.length },
            { label: t`Entities linked`, value: stats?.identity?.total ?? 0 },
            { label: t`Turns captured`, value: Object.values(stats?.episodes || {}).reduce((a, b) => a + b, 0) },
            { label: t`Held back`, value: heldBack },
          ].map((kpi) => (
            <div key={kpi.label} className="glass-panel rounded-2xl p-5">
              <div className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>{kpi.value}</div>
              <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs (Team is Max-only) */}
        <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: 'var(--color-border)' }}>
          {tabs.map((item) => (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className="px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
              style={{
                color: tab === item.key ? 'var(--color-text)' : 'var(--color-text-muted)',
                borderBottom: tab === item.key
                  ? `2px solid ${item.max ? 'var(--color-max-accent)' : 'var(--color-text)'}`
                  : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {item.label}
              {item.max && <MaxBadge />}
            </button>
          ))}
        </div>

        {/* Held-back banner: instructions (quarantined) + sensitive topics */}
        {tab !== 'team' && heldBack > 0 && statusFilter === 'active' && (
          <div className="glass-panel rounded-2xl p-4 flex items-center justify-between gap-4">
            <div className="text-sm" style={{ color: 'var(--color-text)' }}>
              <Trans>
                <span className="font-medium">Memory Guard</span> held back:{' '}
                <Plural value={quarantined.length} one="# looked like an instruction" other="# looked like instructions" />,{' '}
                <Plural value={sensitive.length} one="# touches a sensitive topic" other="# touch sensitive topics" />.
                Instructions are never used in prompts; sensitive notes are recalled only in conversations on the same topic.
              </Trans>
            </div>
            <button
              onClick={() => { setTab('memories'); chooseStatus(quarantined.length > 0 ? 'quarantined' : 'sensitive'); }}
              className="text-sm font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap"
            >
              <Trans>Review</Trans>
            </button>
          </div>
        )}

        {/* ---- Profile ---- */}
        {tab === 'profile' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs max-w-2xl" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>
                  Four short blocks PrivacyPal compiles from your memories and sends once at the start of each conversation.
                  Edit any block; lock it to keep PrivacyPal from rewriting it.
                </Trans>
              </p>
              <button
                onClick={handleRefreshProfile}
                disabled={profileSaving !== null}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 disabled:opacity-50 whitespace-nowrap"
                style={{ color: 'var(--color-text)' }}
              >
                {profileSaving === 'refresh' ? <Trans>Refreshing…</Trans> : <Trans>Refresh now</Trans>}
              </button>
            </div>
            {!profileLoaded ? (
              <div className="loading"><div className="spinner" /></div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {profile.map((block) => {
                  const draft = profileDraft[block.label] ?? { value: block.value, locked: block.locked };
                  const over = draft.value.length > block.charLimit;
                  const dirty = draft.value !== block.value || draft.locked !== block.locked;
                  const refreshedBy = block.updatedBy === 'user' ? t`you` : 'PrivacyPal';
                  const refreshedOn = fmt(block.updatedAt);
                  return (
                    <div key={block.label} className="glass-panel rounded-2xl p-5 flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{PROFILE_LABEL[block.label]}</div>
                          {block.description && (
                            <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{block.description}</div>
                          )}
                        </div>
                        <span className="text-[11px] whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>v{block.version}</span>
                      </div>
                      <textarea
                        value={draft.value}
                        onChange={(e) => setProfileDraft((prev) => ({ ...prev, [block.label]: { ...draft, value: e.target.value } }))}
                        rows={4}
                        placeholder={t`Nothing compiled yet.`}
                        className={`w-full ${INPUT}`}
                        style={INPUT_STYLE}
                      />
                      <div className="flex items-center justify-between gap-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <span className={over ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                          {draft.value.length}/{block.charLimit}
                        </span>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={draft.locked}
                            onChange={(e) => setProfileDraft((prev) => ({ ...prev, [block.label]: { ...draft, locked: e.target.checked } }))}
                            className="h-4 w-4 rounded"
                          />
                          <span>{draft.locked ? <Trans>Locked</Trans> : <Trans>Lock</Trans>}</span>
                        </label>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                          {block.updatedAt
                            ? <Trans>Refreshed on {refreshedOn} by {refreshedBy}</Trans>
                            : <Trans>Not refreshed yet</Trans>}
                        </span>
                        <button
                          onClick={() => handleSaveProfile(block.label)}
                          disabled={!dirty || over || profileSaving !== null}
                          className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {profileSaving === block.label ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ---- Memories ---- */}
        {tab === 'memories' && (
          <>
            {/* Remember something */}
            <div className="glass-panel rounded-2xl p-5">
              <div className="text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}><Trans>Remember something</Trans></div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={rememberText}
                  onChange={(e) => setRememberText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRemember(); }}
                  placeholder={t`e.g. I prefer short answers with a summary first`}
                  className={`flex-1 ${INPUT}`}
                  style={INPUT_STYLE}
                />
                <select
                  value={rememberCategory}
                  onChange={(e) => setRememberCategory(e.target.value as MemoryCategoryV2)}
                  className={INPUT}
                  style={INPUT_STYLE}
                >
                  {MEMORY_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                  ))}
                </select>
                <button
                  onClick={handleRemember}
                  disabled={remembering || !rememberText.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                >
                  <Trans>Remember</Trans>
                </button>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>Stored as a Privacy Twin like every other memory, and marked as written by you.</Trans>
              </p>
            </div>

            {/* Filters */}
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {(['active', 'sensitive', 'archived', 'quarantined', 'all'] as StatusFilter[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => chooseStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-slate-800 text-white dark:bg-white dark:text-slate-900' : CHIP_OFF}`}
                  >
                    {STATUS_FILTER_LABEL[s]}
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-2">
                  {(['all', ...MEMORY_CATEGORIES] as const).map((c) => (
                    <button
                      key={c}
                      onClick={() => setFilter(c)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === c ? CHIP_ON : CHIP_OFF}`}
                    >
                      {c === 'all' ? t`All` : CATEGORY_LABEL[c]}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t`Search memories…`}
                  className={`${INPUT} sm:w-64`}
                  style={INPUT_STYLE}
                />
              </div>
            </div>

            {/* Facts, grouped by the first entity */}
            <div className="glass-panel rounded-2xl overflow-hidden">
              {visible.length === 0 ? (
                <div className="p-10 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  {statusFilter !== 'active'
                    ? <Trans>Nothing here.</Trans>
                    : memoryOn
                      ? <Trans>No memories yet. Keep using your AI apps; memories are distilled in the background.</Trans>
                      : <Trans>Memory is off, so nothing has been remembered.</Trans>}
                </div>
              ) : (
                groupedRows.map((group) => (
                  <div key={group.key ?? '__loose'}>
                    <div
                      className="px-4 sm:px-5 py-2 text-[11px] font-semibold uppercase tracking-wide border-b border-t first:border-t-0 bg-slate-50 dark:bg-white/[0.03]"
                      style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
                    >
                      {group.key ?? t`Everything else`}
                    </div>
                    <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                      {group.items.map(renderFactRow)}
                    </ul>
                  </div>
                ))
              )}
            </div>

            {/* Import */}
            <div className="glass-panel rounded-2xl p-6">
              <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text)' }}><Trans>Bring memory from another AI</Trans></h2>
              <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>
                  Paste the memory export from ChatGPT, Claude or Gemini. It is protected before it leaves the browser:
                  the text is turned into Privacy Twins first, and only that twin-space version is ever stored.
                </Trans>
              </p>
              <div className="flex flex-col gap-3">
                <select
                  value={importPlatform}
                  onChange={(e) => setImportPlatform(e.target.value as MemoryImportPlatform)}
                  className={`${INPUT} sm:w-48`}
                  style={INPUT_STYLE}
                >
                  {IMPORT_PLATFORMS.map((p) => (
                    <option key={p} value={p}>{IMPORT_PLATFORM_NAME[p]}</option>
                  ))}
                </select>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  rows={5}
                  placeholder={t`Paste the exported memory text here`}
                  className={`w-full ${INPUT}`}
                  style={INPUT_STYLE}
                />
                <div>
                  <button
                    onClick={handleImport}
                    disabled={importing || !importText.trim()}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {importing ? <Trans>Importing…</Trans> : <Trans>Import</Trans>}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ---- Timeline ---- */}
        {tab === 'timeline' && (
          <div className="glass-panel rounded-2xl overflow-hidden">
            {!timelineLoaded ? (
              <div className="loading"><div className="spinner" /></div>
            ) : timelineMonths.length === 0 ? (
              <div className="p-10 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>No dated memories yet. Events and plans show up here by month.</Trans>
              </div>
            ) : (
              timelineMonths.map((month) => (
                <div key={month.key}>
                  <div
                    className="px-4 sm:px-5 py-2 text-[11px] font-semibold uppercase tracking-wide border-b border-t first:border-t-0 bg-slate-50 dark:bg-white/[0.03]"
                    style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
                  >
                    {month.label ?? t`Undated`}
                  </div>
                  <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                    {month.items.map((fact) => (
                      <li key={fact.id} className="p-4 sm:p-5 flex gap-4">
                        <div className="w-24 flex-shrink-0 text-[11px] pt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                          {fmt(factDate(fact))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            {renderCategoryChip(fact)}
                            {fact.platform && (
                              <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{fact.platform}</span>
                            )}
                          </div>
                          <div className="text-sm" style={{ color: 'var(--color-text)' }}>{fact.displayText}</div>
                          {renderEntityChips(fact)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}

        {/* ---- People & projects ---- */}
        {tab === 'entities' && (
          !entitiesLoaded ? (
            <div className="loading"><div className="spinner" /></div>
          ) : entities.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
              <Trans>No people or projects yet. They appear once a few memories mention the same name.</Trans>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {entities.map((entity) => (
                <div key={entity.twinHash} className="glass-panel rounded-2xl p-5 flex flex-col gap-3">
                  <div>
                    <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>{entity.displayValue}</div>
                    <div className="text-[11px] mt-0.5 flex flex-wrap gap-x-3" style={{ color: 'var(--color-text-muted)' }}>
                      {entity.entityType && <span>{entity.entityType}</span>}
                      <span><Plural value={entity.factCount} one="# memory" other="# memories" /></span>
                      {entity.lastMentionedAt && (
                        <span><Trans>last mentioned {fmt(entity.lastMentionedAt)}</Trans></span>
                      )}
                    </div>
                  </div>
                  {entity.summary && (
                    <p className="text-xs flex-1" style={{ color: 'var(--color-text)' }}>{entity.summary}</p>
                  )}
                  {entity.aliases.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {entity.aliases.map((a) => (
                        <span key={a} className="px-2 py-0.5 rounded text-[11px] bg-slate-100 dark:bg-white/5" style={{ color: 'var(--color-text-muted)' }}>{a}</span>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setConfirmEntity(entity)} className={`${BTN_DANGER} self-start`}>
                    <Trans>Forget everything about {entity.displayValue}</Trans>
                  </button>
                </div>
              ))}
            </div>
          )
        )}

        {/* ---- Activity (audit trail) ---- */}
        {tab === 'activity' && (
          <div className="glass-panel rounded-2xl p-6">
            <h3 className="font-semibold mb-4" style={{ color: 'var(--color-text)' }}><Trans>Memory audit trail</Trans></h3>
            {!auditLoaded ? (
              <div className="loading"><div className="spinner" /></div>
            ) : audit.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}><Trans>No activity recorded yet.</Trans></p>
            ) : (
              <ul className="space-y-2">
                {audit.map((entry) => {
                  const detail = entry.detail || {};
                  const recall = entry.action === 'recall_injected';
                  const explain = Array.isArray(detail.explain) ? (detail.explain as MemoryRecallExplain[]) : [];
                  const open = expandedAudit.has(entry.id);
                  return (
                    <li key={entry.id} className="text-xs">
                      <div className="flex gap-3">
                        <span style={{ color: 'var(--color-text-muted)' }} className="whitespace-nowrap">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                        <span className="font-medium" style={{ color: 'var(--color-text)' }}>{entry.action}</span>
                        {recall ? (
                          <button
                            onClick={() => toggleAuditRow(entry.id)}
                            className="text-blue-600 dark:text-blue-400 font-medium"
                          >
                            {open ? <Trans>Hide why</Trans> : <Trans>Why these?</Trans>}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)' }} className="truncate">
                            {JSON.stringify(detail)}
                          </span>
                        )}
                      </div>
                      {recall && open && (
                        <div className="mt-2 ml-2 pl-3 border-l space-y-1" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
                          <div>
                            {typeof detail.tokens === 'number' && <span><Trans>{detail.tokens} tokens</Trans></span>}
                            {typeof detail.packConfidence === 'number' && (
                              <span className="ml-3"><Trans>pack confidence {detail.packConfidence.toFixed(2)}</Trans></span>
                            )}
                          </div>
                          {explain.length === 0 ? (
                            <div><Trans>No per-note explanation was recorded.</Trans></div>
                          ) : (
                            explain.map((row, i) => (
                              <div key={`${entry.id}-${i}`} className="flex gap-3 flex-wrap">
                                <span className="font-mono" style={{ color: 'var(--color-text)' }}>{row.factId}</span>
                                <span>{(row.channels || []).join(', ')}</span>
                                <span><Trans>score {Number(row.score ?? 0).toFixed(2)}</Trans></span>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* ---- Team memory (Max only) ---- */}
        {tab === 'team' && isMax && (
          <div className="glass-panel rounded-2xl overflow-hidden" style={{ border: '1px solid var(--color-max-border)' }}>
            <div className="p-4 sm:p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>
                  Memories your team has shared. Values stay Privacy Twins for everyone except the person who
                  shared them; real values never leave the sharer's vault.
                </Trans>
              </p>
              {!multiplayerOn && (
                <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                  <Trans>
                    Team memory is off for you, so none of these are used in your prompts. Turn it on in the settings
                    above.
                  </Trans>
                </p>
              )}
            </div>
            {teamLoading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : teamFacts.length === 0 ? (
              <div className="p-10 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>Nothing shared yet. Use the Share button on one of your memories to give your team stable context.</Trans>
              </div>
            ) : (
              <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {teamFacts.map((fact) => {
                  const mine = !!user?.id && fact.ownerUserId === user.id;
                  return (
                    <li key={fact.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          {renderCategoryChip(fact)}
                          {mine && (
                            <span
                              className="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide"
                              style={{ backgroundColor: 'var(--color-max-bg)', color: 'var(--color-max-text)' }}
                            >
                              <Trans>Shared by you</Trans>
                            </span>
                          )}
                          <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                            {fmt(fact.validAt || fact.observedAt)}
                          </span>
                        </div>
                        <div className="text-sm" style={{ color: 'var(--color-text)' }}>{fact.displayText}</div>
                        {renderEntityChips(fact)}
                      </div>
                      {(mine || isAdmin) && (
                        <div className="flex gap-2 sm:flex-shrink-0">
                          <button onClick={() => handleTeamDelete(fact)} className={BTN_DANGER}>
                            <Trans>Remove</Trans>
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Data controls */}
        <div className="glass-panel rounded-2xl p-6">
          <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text)' }}><Trans>Your data</Trans></h2>
          <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>
              Erasing destroys the encryption keys that make your memory readable: every copy, including exports and
              synced devices, becomes meaningless. It cannot be undone.
            </Trans>
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExport}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10"
              style={{ color: 'var(--color-text)' }}
            >
              <Trans>Export memory</Trans>
            </button>
            <button
              onClick={openAudit}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10"
              style={{ color: 'var(--color-text)' }}
            >
              <Trans>View audit trail</Trans>
            </button>
            <button
              onClick={() => setConfirmErase(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20"
            >
              <Trans>Erase everything</Trans>
            </button>
          </div>
        </div>
      </div>

      {/* Correct modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditing(null)}>
          <div
            className="glass-panel rounded-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--color-surface)' }}
          >
            <h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}><Trans>Correct this memory</Trans></h3>
            <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
              <Trans>The old version is kept as history and marked superseded; nothing is silently rewritten.</Trans>
            </p>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              className={`w-full ${INPUT}`}
              style={INPUT_STYLE}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-lg text-sm bg-slate-100 dark:bg-white/5"
                style={{ color: 'var(--color-text)' }}
              >
                <Trans>Cancel</Trans>
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                <Trans>Save correction</Trans>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmErase}
        title={t`Erase all memory?`}
        message={t`This destroys your memory encryption keys and deletes every stored memory. Exported and synced copies become permanently unreadable. This cannot be undone.`}
        confirmText={t`Erase everything`}
        variant="danger"
        onConfirm={handleErase}
        onCancel={() => setConfirmErase(false)}
      />

      <ConfirmationModal
        isOpen={confirmEntity !== null}
        title={confirmEntity ? t`Forget everything about ${confirmEntity.displayValue}?` : ''}
        message={t`Every memory that mentions them is deleted, along with the entity itself. This cannot be undone.`}
        confirmText={t`Forget`}
        variant="danger"
        onConfirm={handleForgetEntity}
        onCancel={() => setConfirmEntity(null)}
      />

      <ConfirmationModal
        isOpen={confirmDomain !== null}
        title={confirmDomain ? t`Forget everything about ${DOMAIN_LABEL[confirmDomain]}?` : ''}
        message={t`Every memory filed under this topic is deleted. This cannot be undone.`}
        confirmText={t`Forget this topic`}
        variant="danger"
        onConfirm={handleForgetDomain}
        onCancel={() => setConfirmDomain(null)}
      />
    </Layout>
  );
}
